import { useAffiliateCodeGetter } from "./useAffiliateCodeGetter";
import { useCallback } from "react";
import { AffiliateCodeVO, CodeStatus } from "../../types/affiliateCode";
import { useModal } from "../hooks";
import moment from "moment-timezone";
import { useErrorType } from "../useErrorType";
import { useAffiliateCodeCommissionChecker } from "./useAffiliateCodeCommissionChecker";
import {useTranslation} from "react-i18next";

interface Args {
  setCodeErrorMessage: (_: string) => void;
}
export function useAvailableAffiliateCodeGetter({ setCodeErrorMessage }: Args) {
  const { t } = useTranslation("order");
  const { getAffiliateCode } = useAffiliateCodeGetter();
  const { checkAffiliateCodeCommission } = useAffiliateCodeCommissionChecker();
  const { getErrorObject } = useErrorType();
  const { openAlertModal } = useModal();

  const isUnavailable = useCallback((codeVO: AffiliateCodeVO) => {
    if (codeVO.status === CodeStatus.UNAVAILABLE || codeVO.status === CodeStatus.DELETED) return true;

    if (moment.utc(codeVO.availableStartAt).isAfter(moment())) return true;
    if (moment.utc(codeVO.availableEndAt).isBefore(moment())) return true;

    return false;
  }, []);

  const getAvailableAffiliateCode = useCallback(
    async (code: string, cartIdxes: number[]): Promise<AffiliateCodeVO | null> => {
      try {
        const codeVO: AffiliateCodeVO = await getAffiliateCode(code);
        if (isUnavailable(codeVO)) {
          setCodeErrorMessage(t("warning.code.expired"));
          return null;
        }

        await checkAffiliateCodeCommission({
          cartIdxes,
          affiliateCodeIdx: codeVO.idx,
        });

        return codeVO;
      } catch (e) {
        console.error("getAvailableAffiliateCode occurs an error - ", e);
        const errorType = getErrorObject((e as { message: string }).message);
        const errorCode = errorType?.errorCode?.httpCode;

        switch (errorCode) {
          case 404:
            setCodeErrorMessage(t("warning.code.wrongCode"));
            break;
          case 496:
            setCodeErrorMessage(t("warning.code.unusable"));
            break;
          default:
            openAlertModal(null);
            break;
        }

        return null;
      }
    },
    [getAffiliateCode, isUnavailable, setCodeErrorMessage, getErrorObject, openAlertModal, checkAffiliateCodeCommission, t]
  );

  return {
    getAvailableAffiliateCode,
  };
}
